import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Logo from "../../components/Logo";
import consoleApi from "../../lib/apiConsole";
import { fmtErr } from "../../lib/api";

export default function ApiConsoleLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findEmail, setFindEmail] = useState("");
  const [findBusy, setFindBusy] = useState(false);

  const findAccount = async () => {
    if (!findEmail.includes("@")) { toast.error("Enter a valid work email"); return; }
    setFindBusy(true);
    try {
      const { data } = await consoleApi.post("/api-console/auth/find-account", { email: findEmail });
      toast.success(data.message || "If an account exists, the details are on their way.");
      setFindOpen(false);
    } catch (err) {
      toast.error(fmtErr(err, "Could not process the request"));
    } finally {
      setFindBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await consoleApi.post("/api-console/auth/login", form);
      toast.success("Welcome to ShipAny API Console");
      navigate("/console");
    } catch (err) {
      toast.error(fmtErr(err, "Invalid API console credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#071B3A] text-white" data-testid="api-console-login-page">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden overflow-hidden p-12 lg:flex lg:flex-col lg:justify-between">
          <div className="absolute right-[-12rem] top-[-12rem] h-96 w-96 rounded-full border border-cyan-400/20" />
          <div className="absolute bottom-[-10rem] left-[-8rem] h-80 w-80 rounded-full border border-blue-500/20" />
          <Logo dark />
          <div className="relative z-10 max-w-2xl">
            <span className="rounded-md border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 font-mono text-xs uppercase tracking-[0.25em] text-cyan-300">ShipAny API</span>
            <h1 className="mt-8 font-display text-5xl font-bold tracking-tight xl:text-6xl">One API. Every Courier.</h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">Domestic and international shipping infrastructure for ecommerce platforms, marketplaces, ERPs and SaaS products.</p>
            <div className="mt-10 grid max-w-xl grid-cols-3 gap-3">
              {["Sandbox", "Live Rates", "Webhooks"].map((x) => <div key={x} className="rounded-xl border border-[#1E3A66] bg-[#0B1E3D]/80 p-4 font-mono text-xs uppercase tracking-wider text-slate-300">{x}</div>)}
            </div>
          </div>
          <p className="relative z-10 flex items-center gap-2 text-sm text-slate-400"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Enterprise-grade keys, HMAC webhooks and isolated API wallets.</p>
        </section>
        <section className="flex items-center bg-[#F7FAFC] p-6 text-slate-900 sm:p-12">
          <form onSubmit={submit} className="mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-2xl" data-testid="api-console-login-form">
            <div className="lg:hidden"><Logo /></div>
            <div className="mt-8 lg:mt-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-shipany-blue"><KeyRound className="h-3.5 w-3.5" /> API Console</span>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">Developer sign in</h2>
              <p className="mt-2 text-sm text-slate-500">Use the credentials created by ShipAny Admin. This console is separate from merchant login.</p>
            </div>
            <label className="mt-8 block text-xs font-bold uppercase tracking-wider text-slate-500">Work email</label>
            <input data-testid="api-console-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-shipany-blue focus:outline-none focus:ring-4 focus:ring-blue-100" placeholder="developer@company.com" />
            <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
            <input data-testid="api-console-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-shipany-blue focus:outline-none focus:ring-4 focus:ring-blue-100" placeholder="Temporary or updated password" />
            <button data-testid="api-console-login-submit" disabled={loading} className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-shipany-blue px-5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Enter Console
            </button>
            <p className="mt-5 text-center text-xs text-slate-500">Need API access? <Link to="/contact" data-testid="api-console-contact-link" className="font-bold text-shipany-blue hover:underline">Contact ShipAny</Link></p>
            <div className="mt-3 text-center">
              <button type="button" onClick={() => setFindOpen(!findOpen)} data-testid="api-console-find-account-link" className="text-xs font-bold text-shipany-blue hover:underline">Not sure which email your API account uses?</button>
              {findOpen && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left" data-testid="api-console-find-account-panel">
                  <p className="text-xs text-slate-500">Enter your work email — if an API Console account exists for it, we'll email the sign-in details to that inbox.</p>
                  <div className="mt-3 flex gap-2">
                    <input type="email" value={findEmail} onChange={(e) => setFindEmail(e.target.value)} data-testid="api-console-find-email-input" placeholder="you@company.com"
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
                    <button type="button" disabled={findBusy} onClick={findAccount} data-testid="api-console-find-submit"
                      className="rounded-lg bg-shipany-blue px-4 py-2.5 text-xs font-bold text-white disabled:opacity-60">
                      {findBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
